import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api";
import QRCode from "react-qr-code";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import { Edit2, ExternalLink, Download, Loader2, Link as LinkIcon, Plus, Search, RefreshCw } from "lucide-react";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";
import AddQRModal from "../../AddQRModal/AddQRModal";

const QR_TYPE_LABELS = {
    DESK_STAND: { label: "DESK_STAND", className: "bg-indigo-100 text-indigo-800" },
    PVC_CARD: { label: "PVC_CARD", className: "bg-purple-100 text-purple-800" },
    PVC_STAND: { label: "PVC_STAND", className: "bg-teal-100 text-teal-800" },
    pvc: { label: "PVC_CARD", className: "bg-purple-100 text-purple-800" }, // backward compatibility for legacy records
    stand: { label: "DESK_STAND", className: "bg-indigo-100 text-indigo-800" } // backward compatibility for legacy records
};

function QRTypeBadge({ type }) {
    const config = QR_TYPE_LABELS[type] || { label: type, className: "bg-gray-100 text-gray-800" };
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
            {config.label}
        </span>
    );
}

export default function QRManagement() {
    const { selectedPropertyId } = useProperty();
    const [qrs, setQrs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedQR, setSelectedQR] = useState(null);
    const [editUrl, setEditUrl] = useState("");
    const [editUserId, setEditUserId] = useState("");
    const [teamMembers, setTeamMembers] = useState([]);
    const [page, setPage] = useState(1);
    const [isAddQROpen, setIsAddQROpen] = useState(false);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [downloadingId, setDownloadingId] = useState(null);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // QR print template states
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [selectedQRForDownload, setSelectedQRForDownload] = useState(null);
    const [downloadType, setDownloadType] = useState("DESK_STAND");
    const [includePVCFrontDownload, setIncludePVCFrontDownload] = useState(false);
    const [filterType, setFilterType] = useState("");
    const [activeTab, setActiveTab] = useState("physical"); // "physical" or "wallet"
    const [downloadingWalletId, setDownloadingWalletId] = useState(null);
    const [isWalletDownloading, setIsWalletDownloading] = useState(false);

    const isIPhone = () => {
        if (typeof window === "undefined") return false;
        const ua = window.navigator.userAgent.toLowerCase();
        return /iphone|ipad|ipod/.test(ua);
    };

    const triggerWalletDownload = (qrId, shortCode = "") => {
        if (isWalletDownloading) return;
        setIsWalletDownloading(true);
        setDownloadingWalletId(qrId);
        
        // Defer the download by 100ms to let React render the loading spinner overlay first
        setTimeout(() => {
            const baseURL = apiClient.defaults.baseURL || "";
            window.location.href = `${baseURL}/api/qrs/${qrId}/wallet`;
        }, 100);

        setTimeout(() => {
            setIsWalletDownloading(false);
            setDownloadingWalletId(null);
            setIsDownloadModalOpen(false);
        }, 2500);
    };

    // Reset pagination and debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);

        return () => clearTimeout(handler);
    }, [search]);

    const fetchQRs = async () => {
        if (!selectedPropertyId) return;
        try {
            setIsLoading(true);
            const skip = (page - 1) * limit;
            const response = await apiClient.get(`/api/qrs?propertyId=${selectedPropertyId}&limit=${limit}&skip=${skip}&type=${filterType}&search=${debouncedSearch}`);
            if (response.status === 200) {
                setQrs(response.data.result.result);
                setTotal(response.data.result.totalCount || 0);
            }
        } catch (error) {
            toast.error("Failed to fetch QR links");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTeamMembers = async () => {
        if (!selectedPropertyId) return;
        try {
            const response = await apiClient.get(`/user/${selectedPropertyId}`);
            if (response.status === 200) {
                // Filter to only include active members (not invited)
                const members = response.data.result.filter(m => !m.type || m.type !== 'invited');
                setTeamMembers(members);
            }
        } catch (error) {
            console.error("Error fetching team members:", error);
        }
    };

    useEffect(() => {
        fetchQRs();
        fetchTeamMembers();
    }, [selectedPropertyId, page, limit, filterType, debouncedSearch]);

    const handleEdit = (qr) => {
        setSelectedQR(qr);
        setEditUrl(qr.redirectUrl || qr.effectiveRedirectUrl || "");
        setEditUserId(qr.userId || "");
        setIsEditModalOpen(true);
    };

    const handleUpdate = async () => {
        try {
            const response = await apiClient.patch(`/api/qrs/${selectedQR._id}`, {
                userId: editUserId || null,
            });
            if (response.status === 200) {
                toast.success("QR link updated successfully");
                setIsEditModalOpen(false);
                fetchQRs();
            }
        } catch (error) {
            toast.error("Update failed");
        }
    };

    const handleDownload = async (qr, type = "DESK_STAND") => {
        try {
            setDownloadingId(qr._id);
            const response = await apiClient.get(`/api/qrs/${qr._id}/download?type=${type}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            let filename = `QR_${qr.shortCode}_Desk_Stand_127x152.4mm.png`;
            if (type === "PVC_CARD" || type === "pvc") {
                filename = `QR_${qr.shortCode}_PVC_Card_54x85.5mm.png`;
            } else if (type === "PVC_STAND") {
                filename = `QR_${qr.shortCode}_PVC_Stand_100x150mm.png`;
            } else if (type === "DESK_STAND" || type === "stand") {
                filename = `QR_${qr.shortCode}_Desk_Stand_127x152.4mm.png`;
            }
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            if (type === "PVC_CARD" && includePVCFrontDownload) {
                const frontResponse = await apiClient.get("/api/qrs/pvc-front", { responseType: 'blob' });
                const frontUrl = window.URL.createObjectURL(new Blob([frontResponse.data]));
                const frontLink = document.createElement('a');
                frontLink.href = frontUrl;
                frontLink.download = "PVC_Front_Card.png";
                document.body.appendChild(frontLink);
                frontLink.click();
                document.body.removeChild(frontLink);
                window.URL.revokeObjectURL(frontUrl);
            }

            toast.success(
                <div>
                    <p className="font-bold">QR Code downloaded successfully</p>
                    <p className="text-[10px] mt-1 opacity-90">If file is not downloaded please allow multi file download in chrome tab</p>
                </div>
            );
        } catch (error) {
            console.error("Error downloading QR:", error);
            toast.error("Failed to download QR code");
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <div className="bg-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12 min-h-screen">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div className="sm:flex-auto">
                    <h1 className="text-xl font-semibold text-gray-900">QR Codes</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Manage your QR codes. Link them to specific team members for individual employee reviews or set custom redirect destinations.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                    <button
                        type="button"
                        onClick={() => setIsAddQROpen(true)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 shadow-md hover:shadow-indigo-200 transition-all duration-200 transform active:scale-95 w-full sm:w-auto"
                    >
                        <Plus className="h-4 w-4" />
                        Add QR
                    </button>
                </div>
            </div>

            <div className="mt-8 flex flex-col">
                {/* Search and Filter Panel */}
                <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                    {/* Left: Type Filter */}
                    <div className="flex items-center gap-3 self-start md:self-auto">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Type:</span>
                        <select
                            value={filterType}
                            onChange={(e) => {
                                setFilterType(e.target.value);
                                setPage(1);
                            }}
                            className="block rounded-md border-gray-300 py-1.5 pl-3 pr-8 text-sm font-semibold focus:border-indigo-500 focus:ring-indigo-500 bg-white shadow-sm border text-gray-700 cursor-pointer"
                        >
                            <option value="">All Types</option>
                            <option value="DESK_STAND">DESK_STAND</option>
                            <option value="PVC_CARD">PVC_CARD</option>
                            <option value="PVC_STAND">PVC_STAND</option>
                        </select>
                    </div>

                    {/* Right: Search Input & Refresh Button */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto md:justify-end">
                        <div className="relative rounded-md shadow-sm min-w-[300px] w-full sm:w-auto">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <Search className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="block w-full rounded-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                                placeholder="Search by ID, shortcode, or member..."
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            {isLoading ? (
                                <div className="flex items-center text-xs text-gray-400 font-semibold bg-gray-50 px-3 py-1.5 rounded-lg">
                                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin text-indigo-600" />
                                    Loading...
                                </div>
                            ) : (
                                <button
                                    onClick={fetchQRs}
                                    className="inline-flex items-center text-xs font-semibold text-indigo-600 hover:text-indigo-900 bg-indigo-50/50 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-all"
                                    title="Refresh QR inventory"
                                >
                                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                                    Refresh Table
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="hidden md:block -my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg relative">
                            {isLoading && qrs.length > 0 && (
                                <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-10">
                                    <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-150">
                                        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                                        <span className="text-sm font-medium text-gray-600">Updating inventory...</span>
                                    </div>
                                </div>
                            )}
                            <table className="min-w-full divide-y divide-gray-300 table-fixed">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-[50px]">Sr No</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-[230px]">QR Details</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Linked Member</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 text-center w-[70px]">Scans</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Redirect Link</th>
                                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6 w-[100px]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {qrs.length === 0 && isLoading ? (
                                        <tr>
                                            <td colSpan="7" className="py-10 flex justify-center"><LoadingSpinner /></td>
                                        </tr>
                                    ) : qrs.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="py-10 text-center text-gray-500">No QR codes found for this property.</td>
                                        </tr>
                                    ) : (
                                        qrs.map((qr, index) => (
                                            <tr key={qr._id}>
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                    {(page - 1) * limit + index + 1}
                                                </td>
                                                {/* QR Details: ID + ShortCode + Type badge */}
                                                <td className="px-3 py-3">
                                                    <div className="flex flex-col gap-1">
                                                        <a
                                                            href={`https://${qr.fullQRLink}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center text-indigo-600 font-mono text-xs hover:underline"
                                                            title={qr.fullQRLink}
                                                        >
                                                            <LinkIcon className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                                                            <span className="truncate max-w-[180px]">{qr._id}</span>
                                                        </a>
                                                        <div className="flex items-center gap-2">
                                                            <QRTypeBadge type={qr.generatedType} />
                                                            <span className="text-xs text-indigo-600 font-mono font-semibold">{qr.shortCode}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-4 text-sm">
                                                    {qr.userName ? (
                                                        <div className="flex flex-col">
                                                            <span className="font-medium text-gray-900">{qr.userName}</span>
                                                            <span className="text-xs text-gray-500">{qr.userEmail}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                                            Generic
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-4 text-sm text-gray-500 font-medium text-center">
                                                    {qr.numberOfTimeScanned || 0}
                                                </td>
                                                <td className="px-3 py-4 text-sm max-w-[200px]">
                                                    <a
                                                        href={qr.effectiveRedirectUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-indigo-600 hover:underline flex items-center"
                                                        title={qr.effectiveRedirectUrl}
                                                    >
                                                        <span className="truncate">{qr.effectiveRedirectUrl}</span>
                                                        <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
                                                    </a>
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                    <div className="flex items-center justify-end gap-4">
                                                        <button
                                                            onClick={() => {
                                                                if (qr.generatedType === "pvc" || qr.generatedType === "PVC_CARD") {
                                                                    setSelectedQRForDownload(qr);
                                                                    setDownloadType("PVC_CARD");
                                                                    setIncludePVCFrontDownload(false);
                                                                    setIsDownloadModalOpen(true);
                                                                } else {
                                                                    handleDownload(qr, qr.generatedType);
                                                                }
                                                            }}
                                                            disabled={downloadingId === qr._id}
                                                            className={`transition-all transform hover:scale-110 ${downloadingId === qr._id ? "text-indigo-400" : "text-gray-400 hover:text-indigo-600"}`}
                                                            title="Download QR"
                                                        >
                                                            {downloadingId === qr._id ? (
                                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                            ) : (
                                                                <Download className="w-5 h-5" />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => handleEdit(qr)}
                                                            className="text-indigo-600 hover:text-indigo-900 flex items-center font-semibold group transition-all"
                                                        >
                                                            <Edit2 className="w-4 h-4 mr-1 group-hover:scale-110 transition-transform" /> Edit
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="md:hidden space-y-4">
                    {qrs.length === 0 && isLoading ? (
                        <div className="py-10 flex justify-center bg-white rounded-lg shadow-sm border border-gray-200">
                            <LoadingSpinner />
                        </div>
                    ) : qrs.length === 0 ? (
                        <div className="py-10 text-center text-gray-500 bg-white rounded-lg shadow-sm border border-gray-200">
                            No QR codes found for this property.
                        </div>
                    ) : (
                        qrs.map((qr, index) => (
                            <div key={qr._id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 active:border-indigo-200 transition-colors">
                                <div className="flex justify-between items-start mb-4">
                                    <span className="inline-flex items-center justify-center bg-gray-100 text-gray-700 text-[10px] font-bold px-2 py-1 rounded-md">
                                        #{(page - 1) * limit + index + 1}
                                    </span>
                                    <div className="flex items-center gap-1.5 text-gray-900 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-100">
                                        <span className="text-xs font-bold text-indigo-700">{qr.numberOfTimeScanned || 0}</span>
                                        <span className="text-[10px] uppercase font-semibold text-indigo-500">Scans</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-1">QR ID</p>
                                            <a href={`https://${qr.fullQRLink}`} target="_blank" rel="noreferrer" className="text-indigo-600 font-mono font-bold text-sm flex items-center hover:underline group" title={qr.fullQRLink}>
                                                <span className="truncate max-w-[150px]">{qr._id}</span>
                                                <ExternalLink className="w-3.5 h-3.5 ml-1.5 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
                                            </a>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-1">Type</p>
                                            <QRTypeBadge type={qr.generatedType} />
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-1.5">Linked Member</p>
                                        {qr.userName ? (
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs">
                                                    {qr.userName.charAt(0)}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="font-semibold text-gray-900 text-sm truncate">{qr.userName}</span>
                                                    <span className="text-xs text-gray-500 truncate">{qr.userEmail}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="inline-flex items-center rounded-lg bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600 border border-gray-200">
                                                Generic / Shared
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mb-1">Redirect Destination</p>
                                        <a
                                            href={qr.effectiveRedirectUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-indigo-600 hover:underline flex items-center text-sm group"
                                        >
                                            <span className="truncate max-w-[240px]">
                                                {qr.effectiveRedirectUrl}
                                            </span>
                                            <ExternalLink className="w-3.5 h-3.5 ml-1.5 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
                                        </a>
                                    </div>
                                </div>

                                <div className="mt-5 pt-4 border-t border-gray-100 flex gap-3">
                                    <button
                                        onClick={() => {
                                            if (qr.generatedType === "pvc" || qr.generatedType === "PVC_CARD") {
                                                setSelectedQRForDownload(qr);
                                                setDownloadType("PVC_CARD");
                                                setIncludePVCFrontDownload(false);
                                                setIsDownloadModalOpen(true);
                                            } else {
                                                handleDownload(qr, qr.generatedType);
                                            }
                                        }}
                                        disabled={downloadingId === qr._id}
                                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                                            downloadingId === qr._id 
                                                ? "bg-gray-50 text-indigo-400 border-gray-200" 
                                                : "bg-white text-gray-700 border-gray-300 active:bg-gray-50 active:scale-95 shadow-sm"
                                        }`}
                                    >
                                        {downloadingId === qr._id ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span className="text-xs font-semibold">Downloading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                <span className="text-xs font-semibold">Download</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleEdit(qr)}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:bg-indigo-800 active:scale-95 shadow-md shadow-indigo-100 transition-all"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                        <span className="text-xs font-bold">Edit Settings</span>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Pagination */}
            {total > 0 && (
                <div className="mt-6 flex flex-col lg:flex-row items-center justify-between gap-6 bg-white p-5 rounded-2xl shadow-sm border border-gray-200">
                    <div className="text-sm text-gray-500 font-medium whitespace-nowrap">
                        Showing <span className="font-bold text-gray-900">{(page - 1) * limit + 1}</span> to{" "}
                        <span className="font-bold text-gray-900">{Math.min(page * limit, total)}</span> of{" "}
                        <span className="font-bold text-gray-900">{total}</span> QR codes
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-6 w-full lg:w-auto">
                        <div className="flex items-center gap-3 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100 w-full sm:w-auto justify-center">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Rows</label>
                            <select
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                                className="bg-transparent text-sm font-bold text-gray-700 focus:outline-none cursor-pointer"
                            >
                                {[10, 20, 50, 100].map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1 || isLoading}
                                className="flex-1 sm:flex-none px-4 py-2 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:grayscale transition-all"
                            >
                                Previous
                            </button>
                            <div className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-100 min-w-[110px] text-center flex items-center justify-center min-h-[38px]">
                                {isLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                                ) : (
                                    `Page ${page} of ${Math.ceil(total / limit)}`
                                )}
                            </div>
                            <button
                                onClick={() => setPage((p) => Math.min(Math.ceil(total / limit), p + 1))}
                                disabled={page >= Math.ceil(total / limit) || isLoading}
                                className="flex-1 sm:flex-none px-4 py-2 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:grayscale transition-all"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isEditModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
                        <h2 className="text-lg font-bold text-gray-900 mb-2">Edit QR Link</h2>
                        <p className="text-sm text-gray-500 mb-4">Link this QR to a team member or set a custom redirect destination.</p>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Link to Member (Optional)</label>
                                <select
                                    value={editUserId}
                                    onChange={(e) => setEditUserId(e.target.value)}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3 border transition-all"
                                >
                                    <option value="">None (Generic QR)</option>
                                    {teamMembers.map((member) => (
                                        <option key={member._id} value={member._id}>
                                            {member.name || (member.firstName ? `${member.firstName} ${member.lastName || ""}`.trim() : member.email)} ({member.email})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Custom Redirect URL</label>
                                <input
                                    type="text"
                                    value={editUrl}
                                    disabled
                                    className="block w-full rounded-lg border-gray-300 bg-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3 border transition-all cursor-not-allowed text-gray-500"
                                    placeholder="https://maps.google.com/..."
                                />
                                <p className="mt-1 text-[10px] text-gray-400 italic">Editing the redirect link is disabled.</p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdate}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Layout Selection Modal for Individual QR Download */}
            {isDownloadModalOpen && selectedQRForDownload && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => { setIsDownloadModalOpen(false); setActiveTab("physical"); }}>
                    <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-xl font-bold text-gray-900 mb-1">Get Employee Pass</h2>
                        <p className="text-xs text-gray-500 mb-4">
                            For QR Code <strong>{selectedQRForDownload.shortCode}</strong> (Assigned to: {selectedQRForDownload.userName || "Generic"})
                        </p>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-100 mb-5">
                            <button
                                onClick={() => setActiveTab("physical")}
                                className={`flex-1 pb-3 text-sm font-semibold border-b-2 transition-all ${
                                    activeTab === "physical"
                                        ? "border-indigo-600 text-indigo-600"
                                        : "border-transparent text-gray-400 hover:text-gray-600"
                                }`}
                            >
                                Physical Card (PNG)
                            </button>
                            <button
                                onClick={() => setActiveTab("wallet")}
                                className={`flex-1 pb-3 text-sm font-semibold border-b-2 transition-all ${
                                    activeTab === "wallet"
                                        ? "border-indigo-600 text-indigo-600"
                                        : "border-transparent text-gray-400 hover:text-gray-600"
                                }`}
                            >
                                Apple Wallet Pass
                            </button>
                        </div>

                        {activeTab === "physical" ? (
                            <>
                                <label className="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3 mb-6 cursor-pointer hover:bg-indigo-50 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={includePVCFrontDownload}
                                        onChange={(e) => setIncludePVCFrontDownload(e.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                    />
                                    <span className="flex flex-col">
                                        <span className="text-sm font-semibold text-indigo-900">Also download PVC Front Card</span>
                                        <span className="text-xs text-indigo-700 mt-0.5">
                                            Downloads the shared `PVC_Front_Card.png` alongside the unique QR-specific PVC back file.
                                        </span>
                                    </span>
                                </label>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={() => { setIsDownloadModalOpen(false); setActiveTab("physical"); }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsDownloadModalOpen(false);
                                            setActiveTab("physical");
                                            handleDownload(selectedQRForDownload, selectedQRForDownload.generatedType);
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors flex items-center gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download PNG
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center text-center">
                                {isIPhone() ? (
                                    <div className="py-4 flex flex-col items-center gap-3">
                                        <p className="text-sm text-gray-600 mb-2">Add this card directly to your Apple Wallet on this device.</p>
                                        <button
                                            onClick={() => triggerWalletDownload(selectedQRForDownload._id, selectedQRForDownload.shortCode)}
                                            disabled={isWalletDownloading}
                                            className="block relative"
                                        >
                                            <img 
                                                src="/Add_to_Apple_Wallet_badge.svg.png" 
                                                alt="Add to Apple Wallet" 
                                                className={`h-10 w-auto rounded-xl hover:scale-[1.02] transition-transform cursor-pointer ${isWalletDownloading ? "opacity-50 pointer-events-none" : ""}`}
                                            />
                                            {isWalletDownloading && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
                                                    <Loader2 className="h-5 w-5 animate-spin text-white" />
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="py-2 flex flex-col items-center w-full">
                                        <p className="text-sm text-gray-600 mb-4">Scan this QR code with your iPhone to download and add your pass directly to Apple Wallet:</p>
                                        <div className="p-4 bg-white border border-gray-100 rounded-2xl shadow-inner mb-4">
                                            <QRCode
                                                value={`${apiClient.defaults.baseURL}/api/qrs/${selectedQRForDownload._id}/wallet`}
                                                size={160}
                                                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                                viewBox={`0 0 256 256`}
                                            />
                                        </div>
                                        <button
                                            onClick={() => triggerWalletDownload(selectedQRForDownload._id, selectedQRForDownload.shortCode)}
                                            disabled={isWalletDownloading}
                                            className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-1.5 disabled:opacity-50 disabled:no-underline"
                                        >
                                            {isWalletDownloading ? "Downloading..." : "Or download the pass file (.pkpass) directly"}
                                        </button>
                                    </div>
                                )}

                                <div className="flex justify-end w-full border-t border-gray-100 mt-6 pt-4">
                                    <button
                                        onClick={() => { setIsDownloadModalOpen(false); setActiveTab("physical"); }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors w-full sm:w-auto"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isAddQROpen && (
                <AddQRModal
                    onClose={() => {
                        setIsAddQROpen(false);
                        fetchQRs();
                    }}
                    propertyDetails={{ _id: selectedPropertyId }}
                />
            )}
        </div>
    );
}
